/**
 * useStudioTransport — playback state machine for the Studio DAW.
 *
 * Owns nothing new — all state and refs live in the parent engine's `core`
 * bundle. This hook wires up the play/pause/stop/seek callbacks and the
 * requestAnimationFrame tick that advances `position` while playing (and
 * accumulates the live-recording vocal peaks when a take is rolling).
 *
 * It also owns the small effect that syncs `volumes`/`mutes` state onto the
 * shared GainNodes so channel-strip changes are audible in real time.
 */
import { useCallback, useEffect } from 'react';
import { LIVE_PEAKS_PER_SEC, scheduleClip, stopSources } from './studioAudioUtils';

export function useStudioTransport(core) {
  const {
    ctxRef, gainsRef, sourcesRef, playStartRef, rafRef,
    playingRef, recordingRef, clipsRef,
    // mic refs used by the tick's live-peaks accumulator
    analyserRef, analyserBufRef, liveVocalPeaksRef, recordStartPosRef,
    position, setPosition, setIsPlaying,
    volumes, mutes, ensureCtxReady,
  } = core;

  // ---- gain sync ----
  useEffect(() => {
    const g = gainsRef.current;
    if (g.inst) g.inst.gain.value = mutes.inst ? 0 : volumes.inst;
    if (g.vocal) g.vocal.gain.value = mutes.vocal ? 0 : volumes.vocal;
  }, [volumes, mutes, gainsRef]);

  // ---- rAF tick — all refs, so [] deps are fine ----
  const tick = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const pos = playStartRef.current.pos + (ctx.currentTime - playStartRef.current.ctxTime);
    setPosition(Math.max(0, pos));

    // Sample live mic level during recording so the vocal lane paints in real-time
    if (recordingRef.current && analyserRef.current && analyserBufRef.current) {
      const buf = analyserBufRef.current;
      analyserRef.current.getByteTimeDomainData(buf);
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i] - 128) / 128;
        if (v > peak) peak = v;
      }
      const relSec = Math.max(0, pos - recordStartPosRef.current);
      const idx = Math.floor(relSec * LIVE_PEAKS_PER_SEC);
      const peaks = liveVocalPeaksRef.current;
      if (peak > (peaks[idx] || 0)) peaks[idx] = peak;
    }

    const { instrumental: inst, vocal: voc } = clipsRef.current;
    const dur = Math.max(
      inst ? inst.offset + inst.buffer.duration : 0,
      voc ? voc.offset + voc.buffer.duration : 0,
    );
    if (!recordingRef.current && pos >= dur) {
      // reached the end
      stopSources(sourcesRef);
      playingRef.current = false;
      setIsPlaying(false);
      setPosition(dur);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
    // refs only, so exhaustive-deps is fine with []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = useCallback(async (fromPos = null) => {
    const { instrumental: inst, vocal: voc } = clipsRef.current;
    if (!inst && !voc) return;
    const ctx = await ensureCtxReady();
    stopSources(sourcesRef);
    const startPos = fromPos != null ? fromPos : position;
    const when = ctx.currentTime + 0.08;
    scheduleClip(ctx, inst, gainsRef.current.inst, startPos, when, sourcesRef);
    scheduleClip(ctx, voc, gainsRef.current.vocal, startPos, when, sourcesRef);
    playStartRef.current = { ctxTime: when, pos: startPos };
    playingRef.current = true;
    setIsPlaying(true);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [ensureCtxReady, position, tick, clipsRef, gainsRef, sourcesRef, playStartRef, playingRef, rafRef, setIsPlaying]);

  const pause = useCallback(() => {
    const ctx = ctxRef.current;
    if (ctx && playingRef.current) {
      const pos = playStartRef.current.pos + (ctx.currentTime - playStartRef.current.ctxTime);
      setPosition(Math.max(0, pos));
    }
    stopSources(sourcesRef);
    cancelAnimationFrame(rafRef.current);
    playingRef.current = false;
    setIsPlaying(false);
  }, [ctxRef, playingRef, playStartRef, sourcesRef, rafRef, setPosition, setIsPlaying]);

  const stop = useCallback(() => {
    stopSources(sourcesRef);
    cancelAnimationFrame(rafRef.current);
    playingRef.current = false;
    setIsPlaying(false);
    setPosition(0);
  }, [sourcesRef, rafRef, playingRef, setIsPlaying, setPosition]);

  const seek = useCallback((pos) => {
    const clamped = Math.max(0, pos);
    setPosition(clamped);
    if (playingRef.current) play(clamped);
  }, [play, playingRef, setPosition]);

  return { play, pause, stop, seek, tick };
}
